import { supabase } from "@/integrations/supabase/client";

export interface FounderActionInput {
  action: string;
  entity_type?: string;
  entity_id?: string;
  reason: string;
  before_snapshot?: unknown;
  after_snapshot?: unknown;
}

/**
 * Append-only founder action log. Every privileged founder action
 * (impersonation, kill-switch toggle, rule change, force override, reveal)
 * must be recorded here.
 */
export async function logFounderAction(input: FounderActionInput) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { error } = await supabase.from("founder_action_log").insert({
    actor_id: user.id,
    action: input.action,
    entity_type: input.entity_type ?? null,
    entity_id: input.entity_id ?? null,
    reason: input.reason,
    before_snapshot: (input.before_snapshot ?? null) as never,
    after_snapshot: (input.after_snapshot ?? null) as never,
  });
  if (error) throw error;
}

export const PLATFORM_SWITCH_LABELS: Record<string, { label: string; helper: string }> = {
  registrations:   { label: "Public registrations",  helper: "New shipper / transporter / driver signups" },
  offers:          { label: "Carrier offers",        helper: "Match engine can push offers to carriers" },
  bookings:        { label: "Shipment bookings",     helper: "Shippers can confirm new loads" },
  status_updates:  { label: "Status updates",        helper: "Tracking / scan / POD events flowing" },
  payouts:         { label: "Payouts",               helper: "Carrier payouts released to bank" },
  pricing_updates: { label: "Pricing source updates",helper: "Fuel / toll pipelines auto-activate new indices" },
  global_readonly: { label: "Global read-only mode", helper: "Freeze the entire platform (writes blocked)" },
  pilot_mode:                  { label: "Pilot mode",              helper: "Invitation-only Gauteng road-freight pilot" },
  road_booking_enabled:        { label: "Road booking",            helper: "Road is the only live execution mode" },
  multimodal_requests_enabled: { label: "Multimodal requests",     helper: "Air / sea / rail as request-only planned capability" },
  payments_enabled:            { label: "Payments",                helper: "Provider-backed customer payments (requires live PSP)" },
  payouts_enabled:             { label: "Payouts",                 helper: "Provider-backed transporter payouts" },
  insurance_enabled:           { label: "Platform insurance",      helper: "Only ON when a real partner product is bound" },
  tracking_enabled:            { label: "Job-scoped tracking",     helper: "Driver tracking bound to assignment + manifest + device" },
  auto_dispatch_enabled:       { label: "Auto-dispatch",           helper: "Matching engine dispatches offers and backup cascade" },
  notifications_enabled:       { label: "Notifications",           helper: "Transactional email / in-app comms" },
  quote_locking_enabled:       { label: "Quote price locks",       helper: "Immutable confirmed quote / price lock creation" },
};
